import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Socket from '../utils/Socket.config';
import AlertSmall from '../Alerts/AlertSmall';
import printOrderData from './PrintOrderData';
import { BranchOfficeFetchAPI } from '../../api/BranchOffices';
import { MachinesFetchAPI } from '../../api/Machines';
import { PrinterFetchAPI } from '../../api/Printer';
import { CategriesPosFetchAPI } from '../../api/Paymentpost/CategoriesPos';
import { TableSessionsFetchAPI } from '../../api/TableSessions';

// Imprime la comanda de las mesas PRE-PAGO (QR e-commerce).
//
// En pre-pago el pedido pasa a cocina sin que ningun POS lo toque (el backend marca las lineas como
// send_to_kitchen cuando el cliente paga - ver beginPrepayPayment/confirmPaymentClaim), y el backend
// no imprime: la impresion siempre la dispara un POS. Si cada POS abierto de la sucursal imprimiera al
// recibir el evento saldria una comanda por caja, asi que solo imprime LA maquina designada en la
// sucursal (branchoffice.table_kitchen_print_machine_code, configurable en el dashboard y en la pestaña
// "Mesa" de la configuracion del POS). Sin maquina designada no se imprime automaticamente.
//
// Sin UI. Se monta UNA vez en App.js, fuera de <Routes>, igual que KinposRemoteVoidExecutor: las rutas
// son excluyentes y un socket dentro de una pantalla se cortaria al cambiar de pantalla.
//
// Que imprime: el mismo servicio de impresion de siempre (Id_Orden_Imprimir) le pide al backend las
// lineas en send_to_kitchen aun sin imprimir (orders.js#getFilterTempOrderByIdToImpress) y las marca
// impresas, asi que una linea nunca sale dos veces aunque lleguen varios eventos seguidos.
// Unicas mutaciones del pedido que mandan lineas a cocina SIN que ningun POS intervenga en pre-pago: el
// cliente inicia el pago (todo pasa a cocina) o se confirma el pago de lineas sueltas. Cualquier otro
// cambio (agregar/editar productos, pedir o resolver un descuento...) NO debe imprimir, aunque queden
// lineas viejas sin marcar como impresas.
const KITCHEN_SEND_MUTATIONS = ['PREPAY_SENT_TO_KITCHEN', 'PAYMENT_CONFIRMED'];
const CONFIG_STALE_MS = 60 * 1000;
const PRINTERS_STALE_MS = 5 * 60 * 1000;
// Respaldo de reconcilePendingPrints: sin esto, una comanda perdida solo se recupera en la proxima
// reconexion real del socket, que puede tardar horas (o no pasar) si la conexion queda "viva" pero el
// evento puntual se perdio por cualquier otra razon. La consulta es barata (un find de TableSession +
// TempOrder por sucursal, ver getPendingKitchenPrints en el backend), asi que 5s sigue siendo seguro.
const RECONCILE_POLL_MS = 5 * 1000;
// Espera creciente por orden en la reconciliacion (5s, 10s, 20s... hasta 2 min): si el backend sigue
// reportando una orden pendiente despues de enviarla (impresora apagada, servicio de impresion sin poder
// resolverla), reintentarla cada 5s sin pausa solo llena la cola y retrasa las comandas reales que
// vienen detras - pero NUNCA se rinde, en cuanto el problema se arregla sale en el siguiente intento.
const RECONCILE_MAX_BACKOFF_MS = 2 * 60 * 1000;

const readJson = (key) => {
    try {
        return JSON.parse(localStorage.getItem(key));
    } catch (err) {
        return null;
    }
};

const lineKey = (line) => String(line?.line_uuid || line?._id?.$oid || line?._id || '');

function TableKitchenPrinter() {
    const socketRef = useRef(null);
    const designatedRef = useRef(false);
    const configFetchedAtRef = useRef(0);
    const printersRef = useRef({ list: null, fetchedAt: 0 });
    // Lineas ya enviadas a imprimir en esta sesion del POS: evita doble impresion si llegan dos eventos
    // seguidos antes de que el backend alcance a marcarlas is_printed.
    const printedLinesRef = useRef(new Set());
    // Ids de orden con un printComanda en curso ahora mismo, compartido entre el evento en vivo
    // (handleOrderUpdated) y la reconciliacion al reconectar (reconcilePendingPrints). is_printed en el
    // backend solo se marca cuando el puente de impresion local (Android Bridge/servicio Windows)
    // efectivamente pide los datos de la orden - un viaje aparte, DESPUES de que printComanda ya se
    // llamo - asi que mientras ese viaje esta en curso el backend sigue reportando la orden como "sin
    // imprimir". Un deploy tumba todas las conexiones de golpe: si el socket se reconecta justo en esa
    // ventana (p.ej. justo cuando se confirma un pago), sin este guardado la reconciliacion volveria a
    // mandar a imprimir la misma orden que el evento en vivo ya esta imprimiendo - de ahi la comanda
    // duplicada.
    const printingOrdersRef = useRef(new Set());
    const reconcileAttemptsRef = useRef(new Map());
    // Identidad con la que se abrio el socket (maquina|sucursal|token) y como desmontarlo. Cerrar sesion
    // en el POS hace localStorage.clear() + navigate (sin recargar la pagina), asi que sin esto el socket
    // seguiria vivo con la maquina/sucursal/token anteriores: una caja ya liberada seguiria imprimiendo, o
    // un nuevo login en otra maquina no tomaria efecto.
    const identityRef = useRef(null);
    const teardownRef = useRef(null);
    // El componente se monta antes del login (localStorage aun vacio): se reintenta en cada cambio de
    // ruta, sin reconectar si ya hay socket.
    const location = useLocation();

    useEffect(() => {
        const machine = readJson('machine');
        const branchoffice = readJson('branchoffice');
        const business = readJson('business');
        const token = localStorage.getItem('x-access-machine-token');
        const hasSession = Boolean(machine?.code && branchoffice?.code && business?.code && token);
        const identity = hasSession ? `${machine.code}|${branchoffice.code}|${token}` : null;

        // Misma sesion y ya conectado: nada que hacer.
        if (socketRef.current && identity === identityRef.current) return undefined;
        // Sesion cerrada o distinta (otra maquina/sucursal/token): se suelta la anterior.
        if (socketRef.current) teardownRef.current?.();
        if (!hasSession) return undefined;

        const isDesignated = (branch) => Boolean(branch?.table_kitchen_print_machine_code)
            && String(branch.table_kitchen_print_machine_code) === String(machine.code);
        designatedRef.current = isDesignated(branchoffice);

        // La sucursal en localStorage es la del login: un cambio hecho despues en el dashboard (o desde otra
        // caja) no se veria hasta volver a iniciar sesion, asi que se refresca del backend.
        const refreshConfig = async () => {
            configFetchedAtRef.current = Date.now();
            try {
                const response = await BranchOfficeFetchAPI.getBranchOfficesByid(branchoffice.id, token);
                const fresh = response?.data?.data?.branchoffice;
                if (!fresh) return;
                designatedRef.current = isDesignated(fresh);
                const stored = readJson('branchoffice') || {};
                localStorage.setItem('branchoffice', JSON.stringify({
                    ...stored,
                    table_kitchen_print_machine_code: fresh.table_kitchen_print_machine_code ?? null,
                }));
            } catch (err) {
                // Se conserva lo que ya se sabia; se reintenta en el siguiente evento.
            }
        };

        const onLocalConfigChange = (event) => {
            designatedRef.current = isDesignated(event?.detail);
            configFetchedAtRef.current = Date.now();
        };
        window.addEventListener('smartpay:branchoffice-updated', onLocalConfigChange);

        const loadPrinters = async () => {
            const cached = printersRef.current;
            if (cached.list && Date.now() - cached.fetchedAt < PRINTERS_STALE_MS) return cached.list;

            const response = await PrinterFetchAPI.getPrinter(token, machine.id);
            const printers = response?.data?.printers || [];
            for (const printer of printers) {
                try {
                    const categoriesResponse = await CategriesPosFetchAPI.getCategoriesByPrinters(printer.printer_id, token);
                    printer.categories = categoriesResponse?.data?.categories || [];
                } catch (err) {
                    printer.categories = [];
                }
            }
            printersRef.current = { list: printers, fetchedAt: Date.now() };
            return printers;
        };

        // Solo la(s) impresora(s) de cocina, sin ticket de precuenta (mismo criterio que
        // ModalCreatePreOrder.printOrder(onlyComanda) y RfmModals.printPreOrderFromTables).
        const printComanda = async ({ orderId, transactionCode, tableName, amountItems }) => {
            const printers = await loadPrinters();
            const kitchenPrinters = printers.filter((printer) => printer.printer_type === 'Comanda');
            if (kitchenPrinters.length === 0) throw new Error('Esta maquina no tiene impresora de comanda asignada');

            const printerNameByType = (type) => printers.find((printer) => printer.printer_type === type)?.printer_name || '';
            const printerIpByType = (type) => printers.find((printer) => printer.printer_type === type)?.ip || '';
            let ipPrint = 'localhost';
            const externalIp = localStorage.getItem('ip_printer');
            if (externalIp) ipPrint = externalIp;

            const JsonImprimir = [{
                type_ticket: 'precuenta',
                table_name: tableName,
                Printers: kitchenPrinters,
                Nombre_maquina_impresora: printerNameByType('Tickets'),
                Nombre_cocina_impresora: printerNameByType('Comanda'),
                Nombre_zebra_impresora: printerNameByType('Zebra'),
                // Este trabajo es SOLO de comanda (Printers: kitchenPrinters): la IP tiene que ser la de la
                // impresora de cocina, no la de tickets - con la de tickets el servicio recibia los datos
                // bien (el backend respondia 200) pero apuntaba a la impresora equivocada y no salia nada.
                PRINTER_IP: printerIpByType('Comanda'),
                Id_Orden_Imprimir: orderId,
                token,
                transaction_code: transactionCode,
                // Tiene que ser EXACTAMENTE el total de lineas (groups) de la orden, no las pendientes: el
                // servicio de impresion (Windows: "Vamoas a comparar X con Y"; Android:
                // MainActivity#hasMatchingOrderAmount) lo compara con el `amount_item` que devuelve el backend
                // (order.groups.length) y si no coinciden DESCARTA la comanda en silencio - el backend ya la dio
                // por impresa al entregar los datos, asi que nunca sale. Con las pendientes pasaba en cuanto la
                // mesa tenia mas lineas que las que se estaban enviando (un producto pagado y otro no, o la
                // segunda tanda). 0 tampoco sirve: el Android lo toma como "no comparar" pero Windows lo
                // compara literal (0 contra 2) y descarta todo. Que lineas faltan lo decide el backend.
                amount_item: amountItems,
                imprimir_comanda: 'si',
            }];

            // En Android, printOrderData ya entrega el trabajo por el puente nativo (se no-opea sola si
            // window.AndroidBridge no existe). El canal HTTP de abajo se intenta igual, pero si Android
            // ya imprimio, un fallo de ese canal (normal: una tablet Android no corre el servicio local
            // de Windows) no debe tratarse como fallo real - mismo patron que
            // utils/PrintPaidOrder.js#reprintPaidOrder.
            const hasAndroidBridge = typeof window !== 'undefined' && typeof window.AndroidBridge?.printOrder === 'function';
            if (hasAndroidBridge) printOrderData(JsonImprimir);

            try {
                if (ipPrint === 'localhost') {
                    const machineResponse = await MachinesFetchAPI.getmachineByid(machine.id, token);
                    await PrinterFetchAPI.printOrder(ipPrint, JsonImprimir, machineResponse?.data?.data?.windows_ip);
                } else {
                    await PrinterFetchAPI.printByServerMiddle(ipPrint, JsonImprimir);
                }
            } catch (err) {
                if (!hasAndroidBridge) throw err;
            }
        };

        const handleOrderUpdated = async (payload = {}) => {
            // Solo pre-pago; el post-pago lo imprime el mesero al enviar a cocina (RfmModals).
            if (String(payload.payment_mode).toUpperCase() !== 'PRE_PAY') return;
            const order = payload.order;
            if (!order) return;
            const mutation = payload.mutation;
            if (!KITCHEN_SEND_MUTATIONS.includes(mutation?.type) || mutation.already_confirmed) return;

            if (Date.now() - configFetchedAtRef.current > CONFIG_STALE_MS) await refreshConfig();
            if (!designatedRef.current) return;

            const pendingLines = [...(order.groups || []), ...(order.combos || [])].filter((line) => (
                line.kitchen_action === 'send_to_kitchen'
                && line.line_status !== 'CANCELLED'
                && line.is_printed !== true
                && !printedLinesRef.current.has(lineKey(line))
            ));
            if (pendingLines.length === 0) return;

            const orderId = String(order._id?.$oid ?? order._id);
            if (printingOrdersRef.current.has(orderId)) return;

            const keys = pendingLines.map(lineKey);
            keys.forEach((key) => printedLinesRef.current.add(key));

            const tableName = payload.table?.name || payload.table?.code || order.table_name || '';
            printingOrdersRef.current.add(orderId);
            try {
                await printComanda({
                    orderId: order._id?.$oid ?? order._id,
                    transactionCode: order.transaction_code || order.code || '',
                    tableName,
                    amountItems: (order.groups || []).length,
                });
                AlertSmall(`Comanda de la mesa ${tableName} enviada a imprimir`, 'success');
            } catch (err) {
                console.error(err);
                // Se libera para que un evento posterior pueda reintentar.
                keys.forEach((key) => printedLinesRef.current.delete(key));
                AlertSmall(`No se pudo imprimir la comanda de la mesa ${tableName}`, 'warning');
            } finally {
                printingOrdersRef.current.delete(orderId);
            }
        };

        // Recupera comandas que quedaron en send_to_kitchen sin imprimir porque esta maquina no estaba
        // conectada (o se estaba reconectando) justo cuando el backend emitio el aviso en tiempo real -
        // el caso tipico es un pago de Yappy que se confirmo por el webhook (server-to-server) en vez de
        // por el navegador del cliente. Se corre al conectar/reconectar el socket (que es cuando pudo
        // haberse perdido algo) Y en un intervalo mientras la maquina designada siga montada (abajo) -
        // el socket puede seguir "conectado" en apariencia pero perder un evento puntual por cualquier
        // otra razon (error de JS, pestaña en segundo plano, lo que sea), y solo depender de 'connect'
        // no cubre ese caso: la reconexion no siempre ocurre. Ver getPendingKitchenPrints en el backend.
        const reconcilePendingPrints = async () => {
            if (!designatedRef.current) return;
            try {
                const response = await TableSessionsFetchAPI.getPendingKitchenPrints({ branchoffice_code: branchoffice.code }, token);
                const pendingOrders = response?.data?.data?.orders || [];
                for (const pendingOrder of pendingOrders) {
                    const orderId = String(pendingOrder.order_id);
                    // El evento en vivo ya esta imprimiendo esta misma orden ahora mismo - no duplicar.
                    if (printingOrdersRef.current.has(orderId)) continue;
                    const previous = reconcileAttemptsRef.current.get(orderId) || { attempts: 0, nextAt: 0 };
                    if (Date.now() < previous.nextAt) continue;
                    reconcileAttemptsRef.current.set(orderId, {
                        attempts: previous.attempts + 1,
                        nextAt: Date.now() + Math.min(RECONCILE_POLL_MS * (2 ** previous.attempts), RECONCILE_MAX_BACKOFF_MS),
                    });
                    printingOrdersRef.current.add(orderId);
                    try {
                        await printComanda({
                            orderId: pendingOrder.order_id,
                            transactionCode: pendingOrder.transaction_code,
                            tableName: pendingOrder.table_name,
                            // groups_count = total de lineas de la orden (lo que comparan los servicios de
                            // impresion); amount_items solo cuenta las pendientes (backend anterior).
                            amountItems: pendingOrder.groups_count ?? pendingOrder.amount_items,
                        });
                        AlertSmall(`Comanda pendiente de la mesa ${pendingOrder.table_name} enviada a imprimir`, 'success');
                    } catch (err) {
                        console.error(err);
                    } finally {
                        printingOrdersRef.current.delete(orderId);
                    }
                }
            } catch (err) {
                // Best-effort: si falla, se reintenta en la proxima reconexion.
            }
        };

        const onConnect = async () => {
            await refreshConfig();
            await reconcilePendingPrints();
        };

        // Respaldo del guardado "connect" de arriba: el socket puede seguir conectado sin perder esa
        // condicion aunque un evento puntual nunca haya llegado (sin un 'connect'/'disconnect' de por
        // medio no hay otra señal de que algo se perdio). Este intervalo es lo que de verdad garantiza
        // "se imprime si o si" una vez la maquina designada esta abierta - sin el, solo se reintenta en
        // cada reconexion real, que puede no volver a pasar en toda la sesion.
        let reconcileInFlight = false;
        const reconcileIntervalId = setInterval(() => {
            if (reconcileInFlight) return;
            reconcileInFlight = true;
            reconcilePendingPrints().finally(() => { reconcileInFlight = false; });
        }, RECONCILE_POLL_MS);

        // Misma sala que usa Reservation.jsx: business_{code}_branchoffice_{code}.
        const socket = Socket({ business_code: business.code, branchoffice_code: branchoffice.code });
        socketRef.current = socket;
        identityRef.current = identity;
        socket.on('tableSessionOrderUpdated', handleOrderUpdated);
        socket.on('connect', onConnect);
        onConnect();

        teardownRef.current = () => {
            clearInterval(reconcileIntervalId);
            const removeListener = socket.off ? socket.off.bind(socket) : socket.removeListener?.bind(socket);
            removeListener?.('tableSessionOrderUpdated', handleOrderUpdated);
            removeListener?.('connect', onConnect);
            socket.disconnect();
            window.removeEventListener('smartpay:branchoffice-updated', onLocalConfigChange);
            socketRef.current = null;
            identityRef.current = null;
            designatedRef.current = false;
            printersRef.current = { list: null, fetchedAt: 0 };
            printedLinesRef.current = new Set();
            printingOrdersRef.current = new Set();
            reconcileAttemptsRef.current = new Map();
        };

        // Vive toda la sesion del POS: no se suelta al cambiar de ruta, solo si cambia la identidad de
        // arriba o se desmonta la app.
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => () => teardownRef.current?.(), []);

    return null;
}

export default TableKitchenPrinter;
